import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Currency / Date / Time / Timezone defaults for the whole app.
 *
 * These values physically live on the CompanyProfile document
 * (`/api/company/profile`), the same place the CompanyProfile screen
 * used to edit them. Moved here so they live alongside the other
 * app-wide dropdown values in Settings > General.
 */
export default function ApplicationDefaults({ canEdit = true }) {
  const { t } = useTranslation();
  const [profile, setProfile] = useState({ currency: '', date_format: '', time_format: '', timezone: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/company/profile`);
      setProfile({
        currency: data.currency || 'USD',
        date_format: data.date_format || 'MM/DD/YYYY',
        time_format: data.time_format || '24h',
        timezone: data.timezone || 'UTC',
        _raw: data,
      });
    } catch { toast.error(t('settings.appDefaults.loadError')); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const body = { ...(profile._raw || {}), currency: profile.currency, date_format: profile.date_format, time_format: profile.time_format, timezone: profile.timezone };
      await axios.put(`${API}/company/profile`, body);
      toast.success(t('settings.appDefaults.saved'));
    } catch { toast.error(t('settings.appDefaults.saveError')); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  if (loading) return <Card><CardContent className="p-6 text-sm text-slate-500">{t('settings.appDefaults.loading')}</CardContent></Card>;

  return (
    <Card data-testid="application-defaults-card">
      <CardHeader>
        <CardTitle>{t('settings.appDefaults.title')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={save} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">{t('settings.appDefaults.currency')}</label>
              <Select value={profile.currency} onValueChange={(v) => set('currency', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="app-defaults-currency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">{t('auto.set.usdUsDollar')}</SelectItem>
                  <SelectItem value="EUR">{t('auto.set.eurEuro')}</SelectItem>
                  <SelectItem value="GBP">{t('auto.set.gbpBritishPound')}</SelectItem>
                  <SelectItem value="PLN">{t('auto.set.plnPolishZloty')}</SelectItem>
                  <SelectItem value="CAD">{t('auto.set.cadCanadianDollar')}</SelectItem>
                  <SelectItem value="AUD">{t('auto.set.audAustralianDollar')}</SelectItem>
                  <SelectItem value="JPY">{t('auto.set.jpyJapaneseYen')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('settings.appDefaults.dateFormat')}</label>
              <Select value={profile.date_format} onValueChange={(v) => set('date_format', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="app-defaults-date-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="MM/DD/YYYY">{t('auto.set.mmDdYyyy')}</SelectItem>
                  <SelectItem value="DD/MM/YYYY">{t('auto.set.ddMmYyyy')}</SelectItem>
                  <SelectItem value="YYYY-MM-DD">{t('auto.set.yyyyMmDd')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('settings.appDefaults.timeFormat')}</label>
              <Select value={profile.time_format} onValueChange={(v) => set('time_format', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="app-defaults-time-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="12h">{t('settings.appDefaults.time12h')}</SelectItem>
                  <SelectItem value="24h">{t('settings.appDefaults.time24h')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">{t('settings.appDefaults.timezone')}</label>
              <Select value={profile.timezone} onValueChange={(v) => set('timezone', v)} disabled={!canEdit}>
                <SelectTrigger data-testid="app-defaults-timezone"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="UTC">{t('auto.set.utc')}</SelectItem>
                  <SelectItem value="America/New_York">{t('auto.set.easternTime')}</SelectItem>
                  <SelectItem value="America/Chicago">{t('auto.set.centralTime')}</SelectItem>
                  <SelectItem value="America/Denver">{t('auto.set.mountainTime')}</SelectItem>
                  <SelectItem value="America/Los_Angeles">{t('auto.set.pacificTime')}</SelectItem>
                  <SelectItem value="Europe/London">{t('auto.set.london')}</SelectItem>
                  <SelectItem value="Europe/Warsaw">{t('auto.set.warsaw')}</SelectItem>
                  <SelectItem value="Europe/Paris">{t('auto.set.paris')}</SelectItem>
                  <SelectItem value="Asia/Tokyo">{t('auto.set.tokyo')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {canEdit && (
            <Button type="submit" disabled={saving} data-testid="save-application-defaults">
              {saving ? t('settings.appDefaults.saving') : t('settings.appDefaults.save')}
            </Button>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
